/**
 * @file CartRowsGroup.tsx
 * @description Группа «Строки товара» «Вида корзины» (phase19.16, SB120
 * этап 4; uiux-cart §4, db-design-sb120-stage4 §1.3):
 *  - размер фото 48–120 px (↺ 80; меньше 56 — предупреждение);
 *  - скругление фото и «Итого» 0–24 px (↺ 8) и ступень «Как у сайта»
 *    (`radius: null` — скругление карточек из «Оформления»). Кнопки −/+ —
 *    ¾ этого числа (`controlShare`).
 */

import { SITE_THEME_LIMITS } from "@chaiberi/shared";

import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import type { NumberPreset } from "../../site-fields/NumberPresets";
import { useCartLook } from "./cart-look-context";
import { CartFields } from "./CartFields";
import { CartNumberField } from "./CartNumberField";
import { groupHasWarning, rowsSummary } from "../../../lib/site-view/cart-look";
import { cn } from "@/lib/utils";

const L = SITE_THEME_LIMITS.cart;

const PHOTO_PRESETS: readonly NumberPreset[] = [
  { label: "56", value: 56 },
  { label: "64", value: 64 },
  { label: "Как было 80", value: L.photo.default },
  { label: "96", value: 96 },
  { label: "120", value: 120 },
];
const RADIUS_PRESETS: readonly NumberPreset[] = [
  { label: "0", value: 0 },
  { label: "4", value: 4 },
  { label: "Как было 8", value: L.radius.default },
  { label: "16", value: 16 },
];

export function CartRowsGroup() {
  const { cart, theme, warnings, change, preview, readOnly } = useCartLook();
  const siteRadius = Math.round(theme.shapes.radius.card);
  const asSite = cart.radius === null;
  const radius = cart.radius ?? siteRadius;
  return (
    <SettingsGroup id="rows" title="Строки товара" tocLabel="Строки" hint="Фото и скругление в строках корзины." summary={rowsSummary(cart)} status={groupHasWarning(warnings, "rows") ? "warning" : undefined}>
      <CartFields>
        <CartNumberField
          label="Размер фото"
          path="cart.photo"
          value={cart.photo}
          min={L.photo.min}
          max={L.photo.max}
          def={L.photo.default}
          unit="px"
          presets={PHOTO_PRESETS}
          patch={(photo) => ({ photo })}
        />
        <div className="flex min-w-0 flex-col gap-2">
          <NumberSliderField
            label="Скругление"
            value={radius}
            inherit={{ value: L.radius.default, label: `${L.radius.default} px` }}
            min={L.radius.min}
            max={L.radius.max}
            step={1}
            integer
            unit="px"
            presets={RADIUS_PRESETS}
            disabled={readOnly}
            onChange={(v) => change({ radius: v ?? L.radius.default })}
            onPreview={(v) => preview({ radius: v ?? L.radius.default })}
          />
          <button
            type="button"
            aria-pressed={asSite}
            onClick={() => change({ radius: null })}
            className={cn(
              "inline-flex h-12 items-center self-start rounded-lg px-3 text-sm font-medium outline-none can-hover:h-8 can-hover:px-2",
              "focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              asSite ? "bg-surface-lowest text-on-surface [box-shadow:inset_0_0_0_2px_var(--primary)]" : "bg-surface-low text-on-surface-variant hover:text-on-surface",
            )}
          >
            Как у сайта · {siteRadius} px
          </button>
          <FieldHint className="mt-0">
            {asSite ? `Как у сайта — скругление карточек из «Оформления» (${siteRadius} px), меняется вместе с ним. ` : ""}
            Кнопки −/+ — ¾ этого числа.
          </FieldHint>
        </div>
      </CartFields>
    </SettingsGroup>
  );
}
